import { useCallback } from "react";
import { useToast } from "components-lib";
import { axiosInstance } from "config/api";

/**
 * Custom hook for managing all patient action handlers
 * Centralizes business logic and API calls for the PatientDetails component
 * Handles consultation flow, prescription creation, and data management
 */
export const usePatientActions = (patient, patientData) => {
  const toast = useToast();

  // Handle consultation type changes
  const handleConsultationTypeChange = useCallback((type) => {
    patientData.setConsultationType((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, [patientData]);

  // Handle online consultation request
  const handleOnlineConsultation = useCallback(() => {
    console.log("Online consultation requested");
  }, []);

  // Date calculation utilities
  const calculateToDate = useCallback((fromDate, duration) => {
    if (!fromDate || !duration) return "";

    const date = new Date(fromDate);
    const [value, unit] = duration.split(" ");

    switch(unit) {
      case "day":
      case "days":
        date.setDate(date.getDate() + parseInt(value));
        break;
      case "week":
      case "weeks":
        date.setDate(date.getDate() + (parseInt(value) * 7));
        break;
      case "month":
      case "months":
        const months = parseFloat(value);
        const wholeMonths = Math.floor(months);
        const decimalMonths = months - wholeMonths;
        date.setMonth(date.getMonth() + wholeMonths);
        if (decimalMonths > 0) {
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const additionalDays = Math.round(daysInMonth * decimalMonths);
          date.setDate(date.getDate() + additionalDays);
        }
        break;
      default:
        return "";
    }

    return date.toISOString().split('T')[0];
  }, []);

  const calculateFromDate = useCallback((toDate, duration) => {
    if (!toDate || !duration) return "";

    const date = new Date(toDate);
    const [value, unit] = duration.split(" ");

    switch(unit) {
      case "day":
      case "days":
        date.setDate(date.getDate() - parseInt(value));
        break;
      case "week":
      case "weeks":
        date.setDate(date.getDate() - (parseInt(value) * 7));
        break;
      case "month":
      case "months":
        const months = parseFloat(value);
        const wholeMonths = Math.floor(months);
        const decimalMonths = months - wholeMonths;
        date.setMonth(date.getMonth() - wholeMonths);
        if (decimalMonths > 0) {
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const additionalDays = Math.round(daysInMonth * decimalMonths);
          date.setDate(date.getDate() - additionalDays);
        }
        break;
      default:
        return "";
    }

    return date.toISOString().split('T')[0];
  }, []);

  // Enhanced medicine management handlers with improved auto-calculation
  const handleFromDateChange = useCallback((e) => {
    const newFromDate = e.target.value;
    patientData.setFromDate(newFromDate);
    
    // Auto-calculate "To" date when "From" date is provided and duration exists
    if (newFromDate && patientData.newMedicineData.duration) {
      const calculatedToDate = calculateToDate(newFromDate, patientData.newMedicineData.duration);
      patientData.setToDate(calculatedToDate);
    }
    // If no duration is set but "From" date is provided, keep the from date
    // User can select duration later to auto-calculate "To" date
  }, [patientData, calculateToDate]);

  const handleToDateChange = useCallback((e) => {
    const newToDate = e.target.value;
    patientData.setToDate(newToDate);
    
    // Auto-calculate "From" date when "To" date is provided and duration exists
    if (newToDate && patientData.newMedicineData.duration) {
      const calculatedFromDate = calculateFromDate(newToDate, patientData.newMedicineData.duration);
      patientData.setFromDate(calculatedFromDate);
    }
  }, [patientData, calculateFromDate]);

  const handleDurationChange = useCallback((e) => {
    const newDuration = e.target.value;
    patientData.setNewMedicineData({ ...patientData.newMedicineData, duration: newDuration });

    // Priority 1: If "From" date exists, calculate "To" date based on duration
    if (patientData.fromDate && newDuration) {
      const calculatedToDate = calculateToDate(patientData.fromDate, newDuration);
      patientData.setToDate(calculatedToDate);
    }
    // Priority 2: If "To" date exists but no "From" date, calculate "From" date
    else if (patientData.toDate && newDuration && !patientData.fromDate) {
      const calculatedFromDate = calculateFromDate(patientData.toDate, newDuration);
      patientData.setFromDate(calculatedFromDate);
    }
    // Priority 3: If no dates are set, set "From" date to today and calculate "To" date
    else if (newDuration && !patientData.fromDate && !patientData.toDate) {
      const today = new Date().toISOString().split('T')[0];
      patientData.setFromDate(today);
      const calculatedToDate = calculateToDate(today, newDuration);
      patientData.setToDate(calculatedToDate);
    }
  }, [patientData, calculateToDate, calculateFromDate]);

  const handleAddMedicineData = useCallback(() => {
    const fromToString = patientData.fromDate && patientData.toDate ? 
      `${patientData.fromDate} to ${patientData.toDate}` : "";
    
    // Validate all required fields including howToTake for complete medicine table structure
    // if (patientData.newMedicineData.name && patientData.newMedicineData.type && 
    //     patientData.newMedicineData.dosage && patientData.newMedicineData.frequency && 
    //     patientData.newMedicineData.duration && patientData.newMedicineData.howToTake && 
    //     fromToString) {
      
      patientData.setMedicinesData([...patientData.medicinesData, {
        ...patientData.newMedicineData,
        no: patientData.medicinesData.length + 1,
        fromTo: fromToString
      }]);
      
      // Reset form with all fields including howToTake
      patientData.setNewMedicineData({ 
        name: "", 
        type: "", 
        dosage: "", 
        frequency: "", 
        duration: "", 
        howToTake: "",
        fromTo: "" 
      });
      patientData.setFromDate("");
      patientData.setToDate("");
    // } else {
    //   // alert("Please fill in all fields including medicine name, how to take, and dates.");
    // }
  }, [patientData]);

  const handleDeleteMedicine = useCallback((index) => {
    const updatedMedicines = patientData.medicinesData.filter((_, i) => i !== index);
    patientData.setMedicinesData(updatedMedicines);
  }, [patientData]);

  // Next visit handlers
  const calculateNextVisitDate = useCallback((duration) => {
    if (!duration) return "";

    const date = new Date();
    const [value, unit] = duration.split(" ");

    switch(unit) {
      case "day":
      case "days":
        date.setDate(date.getDate() + parseInt(value));
        break;
      case "week":
      case "weeks":
        date.setDate(date.getDate() + (parseInt(value) * 7));
        break;
      case "month":
      case "months":
        const months = parseFloat(value);
        const wholeMonths = Math.floor(months);
        const decimalMonths = months - wholeMonths;
        date.setMonth(date.getMonth() + wholeMonths);
        if (decimalMonths > 0) {
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const additionalDays = Math.round(daysInMonth * decimalMonths);
          date.setDate(date.getDate() + additionalDays);
        }
        break;
      default:
        return "";
    }

    return date.toISOString().split('T')[0];
  }, []);

  const handleNextVisitDurationChange = useCallback((e) => {
    const newDuration = e.target.value;
    patientData.setNextVisitDuration(newDuration);
    if (newDuration) {
      const calculatedDate = calculateNextVisitDate(newDuration);
      patientData.setNextVisitDate(calculatedDate);
    }
  }, [patientData, calculateNextVisitDate]);

  // Investigation handlers
  const handleAddInvestigation = useCallback(() => {
    if (patientData.investigation.trim()) {
      patientData.setInvestigationsList([
        ...patientData.investigationsList, 
        patientData.investigation.trim()
      ]);
      patientData.setInvestigation("");
    }
  }, [patientData]);

  const handleDeleteInvestigation = useCallback((index) => {
    patientData.setInvestigationsList(
      patientData.investigationsList.filter((_, i) => i !== index)
    );
  }, [patientData]);

  // parameters handler
  const handleAddparameters = useCallback(async () => {
    // try {
    //   const parametersFromStorage = JSON.parse(localStorage.getItem('unsyncedparameters') || '{}');
      
    //   let currentparameters = {};
    //   try {
    //     const parametersResponse = await axiosInstance.get(`api/parameters/byAppointment?appointment_id=${patient.appointment_id}`);
    //     if (parametersResponse.data) {
    //       currentparameters = parametersResponse.data;
    //     }
    //   } catch (error) {
    //     console.error('Error fetching current parameters:', error);
    //   }

    //   // { changed code }
    //   // Build a parameters array compatible with:
    //   // { appointment_id: X, parameters: [ { id, vital_id, name, unit, value, created_at, updated_at }, ... ] }
    //   const parameterDefinitions = [
    //     { key: 'height', name: 'Height', unitKey: 'heightUnit', defaultUnit: 'cm', vital_id: null },
    //     { key: 'weight', name: 'Weight', unitKey: 'weightUnit', defaultUnit: 'kg', vital_id: null },
    //     { key: 'temperature', name: 'Temperature', unitKey: 'temperatureUnit', defaultUnit: '°C', vital_id: null },
    //     { key: 'blood_pressure', name: 'Blood Pressure', unitKey: 'bloodPressureUnit', defaultUnit: 'mmHg', vital_id: null },
    //     { key: 'blood_sugar', name: 'Blood Sugar', unitKey: 'bloodSugarUnit', defaultUnit: 'mg/dL', vital_id: null },
    //     { key: 'spO2', name: 'SpO2', unitKey: null, defaultUnit: null, vital_id: null },
    //     { key: 'pulse_rate', name: 'Pulse Rate', unitKey: null, defaultUnit: null, vital_id: null }
    //   ];

    //   // if currentparameters contains an array of parameter objects, use it to preserve ids/vital_ids/timestamps
    //   const existingParameterArray = Array.isArray(currentparameters.parameters) ? currentparameters.parameters : [];

    //   const parameters = parameterDefinitions.map(def => {
    //     const storedVal = parametersFromStorage[def.key];
    //     const currentVal = currentparameters[def.key];
    //     const value = (storedVal !== undefined && storedVal !== '') ? storedVal
    //                   : (currentVal !== undefined && currentVal !== null && currentVal !== '' ? currentVal : '');

    //     const unit = (def.unitKey && (parametersFromStorage[def.unitKey] || currentparameters[def.unitKey])) || def.defaultUnit || null;

    //     const existingObj = existingParameterArray.find(p => {
    //       // try to match by name or vital_id when available
    //       if (def.vital_id && p.vital_id === def.vital_id) return true;
    //       return p.name === def.name;
    //     });

    //     return {
    //       id: existingObj?.id ?? null,
    //       vital_id: existingObj?.vital_id ?? def.vital_id ?? null,
    //       name: def.name,
    //       unit,
    //       value: value !== null && value !== undefined ? String(value) : '',
    //       created_at: existingObj?.created_at ?? null,
    //       updated_at: existingObj?.updated_at ?? null
    //     };
    //   }).filter(p => p.value !== '' && p.value !== null); // only include parameters that actually have a value

    //   const payload = {
    //     appointment_id: patient.appointment_id,
    //     parameters
    //   };

    //   // send refactored payload
    //   const response = await axiosInstance.post('api/parameters/addAppointmentParameters', payload);

    //   if (response.data) {
    //     toast({
    //       title: "parameters Added",
    //       description: "parameters have been saved successfully",
    //       status: "success",
    //       duration: 3000,
    //       isClosable: true,
    //     });

    //     localStorage.removeItem('unsyncedparameters');
    //     return true;
    //   }
    // } catch (error) {
    //   console.error('Error adding parameters:', error);
    //   toast({
    //     title: "Error",
    //     description: "Failed to add parameters: " + (error.response?.data?.message || error.message),
    //     status: "error",
    //     duration: 3000,
    //     isClosable: true,
    //   });
    //   return false;
    // }
  }, [patient, toast]);

  // Consultation end handlers
  const handleEndConsultation = useCallback(async () => {
    patientData.setIsEndConsultationModalOpen(true);
  }, [patientData]);

  
  const updateAppointmentStatus = useCallback(async () => {
    try {
      const appointmentId = patient.appointment_id || patient.id;
      if (!appointmentId) {
        throw new Error('No appointment ID found');
      }

      const response = await axiosInstance.post('/api/appointment/updateApptStatus', {
        id: appointmentId,
        status: 'COMPLETED'
      });

      if (response.data) {
        const storedData = JSON.parse(localStorage.getItem('appointments')) || [];
        const updatedData = storedData.map((p) =>
          (p.patient_id === patient.patient_id || p.id === appointmentId) ? 
          { ...p, status: "COMPLETED" } : p
        );
        localStorage.setItem('appointments', JSON.stringify(updatedData));

        toast({
          title: "Consultation Ended",
          description: "The consultation has been marked as completed successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update appointment status. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [patient, toast]);

  // Preview modal handlers
  const onOpenPreviewModal = useCallback((consultationData = null) => {
    // Store prescription data if provided
    if (consultationData) {
      console.log('🔥 Storing prescription data for preview:', consultationData);
      patientData.setPrescriptionData(consultationData);
    }
    patientData.setIsPreviewOpen(true);
  }, [patientData]);

  const onClosePreviewModal = useCallback(() => {
    patientData.setIsPreviewOpen(false);
    // Clear prescription data when closing
    patientData.setPrescriptionData(null);
  }, [patientData]);

  return {
    // Consultation handlers
    handleConsultationTypeChange,
    handleOnlineConsultation,
    handleEndConsultation,
    updateAppointmentStatus,

    // Medicine handlers
    handleFromDateChange,
    handleToDateChange,
    handleDurationChange,
    handleAddMedicineData,
    handleDeleteMedicine,

    // Next visit handlers
    handleNextVisitDurationChange,

    // Investigation handlers
    handleAddInvestigation,
    handleDeleteInvestigation,

    // parameters handler
    handleAddparameters,

    // Modal handlers
    onOpenPreviewModal,
    onClosePreviewModal,

    // Utility
    toast
  };
};